import _ from 'underscore';

import { zeigeKleineNachricht } from '../systemNachricht.js';

class StammdatenService {
	constructor() {
		this._sort = 0;
		this.postenListe = [];
		this.mitarbeiter = [];
		this.qualifikationen = [];
		this.unternehmen = undefined;
		const params = new URLSearchParams(window.location.search);
		const betriebsstaetteID = params.get('BetriebsstaetteID');
		this.aktuelleBS = betriebsstaetteID;
	}

	get sort() {
		return this._sort;
	}

	set sort(sortierung) {
		this._sort = sortierung;
	}

	get aktuelleBetriebsstaette() {
		return this.aktuelleBS;
	}

	set aktuelleBetriebsstaette(betriebID) {
		this.aktuelleBS = betriebID;
	}

	/**
	 * Gibt uns den Wert zurück ob die Qualifikationen in der ausgewählten Betriebsstätte ausgeblendet werden müssen.
	 * - DienstplanQualifikationenAusblenden
	 * @returns true/false
	 */
	async getQualifikationenAusblenden() {
		if (!this.unternehmen) {
			await this.getUnternehmensdaten();
		}
		const betrieb = this.unternehmen.Betriebsstaette.find((bs) => _.isEqual(bs._id, this.aktuelleBS));
		if (_.isEmpty(betrieb)) {
			return false;
		}
		return betrieb.DienstplanQualifikationenAusblenden;
	}

	async getUnternehmensdaten() {
		// aktualisieren nur, falls das Unternehmen noch undefined ist
        if (!this.unternehmen) {
            try {
                const response = await fetch('/neolohn/api/unternehmen');
                if (response.ok) {
                    const result = await response.json();
                    this.unternehmen = result;
                }
            } catch (error) {
                console.log(error);
                return false;
            }
        }
        return this.unternehmen;
	}

	/**
	 * Ruft alle Mitarbeiter der aktuellen Betriebsstätte vom dem Server auf
	 * @param {boolean} refresh manchmal wollen wir die Daten Neuladen, z.B. bei Betriebsstättenwechsel
	 * @returns Serverantwort
	 */
	async getMitarbeiter(refresh = false) {
		if (!refresh && this.mitarbeiter.length > 0) {
			switch (this._sort) {
				case -1:
					this.mitarbeiter.sort((a, b) => (a.Personalien.Nachname + a.Personalien.Vorname).localeCompare((b.Personalien.Nachname + b.Personalien.Vorname)));
					break;
				case 1:
					this.mitarbeiter.sort((a, b) => (b.Personalien.Nachname + b.Personalien.Vorname).localeCompare((a.Personalien.Nachname + a.Personalien.Vorname)));
					break;
				default:
					// Wir sortieren erst nach der vordefinierten Sortierreihenfolge und dann nach dem Nachnamen
					this.mitarbeiter
						.sort((a, b) => a.Personalien.Sortierreihenfolge - b.Personalien.Sortierreihenfolge || (a.Personalien.Nachname + a.Personalien.Vorname).localeCompare((b.Personalien.Nachname + b.Personalien.Vorname)));
					break;
			}
			return this.mitarbeiter;
		}
		try {
			const response = await fetch(`/dienstplan/api/mitarbeiter?BetriebsstaetteID=${this.aktuelleBS}`);
			if (response.ok) {
				const result = await response.json();
				this.mitarbeiter = result;
				// Wir sortieren erst nach der vordefinierten Sortierreihenfolge und dann nach dem Nachnamen
				this.mitarbeiter.sort((a, b) => a.Personalien.Sortierreihenfolge - b.Personalien.Sortierreihenfolge || (a.Personalien.Nachname + a.Personalien.Vorname).localeCompare((b.Personalien.Nachname + b.Personalien.Vorname)));
				return this.mitarbeiter;
			}
			return null;
		} catch (error) {
			console.log(error);
			throw error;
		}
	}

	/**
	 * Ruft alle Qualifikationen von dem Server auf
	 * @returns Serverantwort
	 */
	async getQualifikationen() {
		if (this.qualifikationen.length > 0) {
			return this.qualifikationen;
		}
		try {
			const response = await fetch('/dienstplan/api/qualifikationen');
			if (response.ok) {
				const result = await response.json();
				this.qualifikationen = result;
				return result;
			}
			return null;
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Ruft alle Posten der aktuellen Betriebsstätte von dem Server auf
	 * @param {boolean} refresh manchmal wollen wir die Daten Neuladen, z.B. bei Betriebsstättenwechsel
	 * @returns Serverantwort
	 */
	async getPosten(refresh = false) {
		if (!refresh && this.postenListe.length > 0) {
			return this.postenListe;
		}
		try {
			const response = await fetch(`/dienstplan/api/posten?BetriebsstaetteID=${this.aktuelleBS}`);
			if (response.ok) {
				const result = await response.json();
				// nur verwendete Posten anzeigen...
				this.postenListe = result.filter((p) => p.Verwenden);
				this.postenListe.sort((a, b) => a.Posten.localeCompare(b.Posten));
				return this.postenListe;
			}
			return null;
		} catch (error) {
			console.log(error);
			return null;
		}
	}

	/**
	 * Ruft alle Dienstplan Tagesnotizen der Woche auf.
	 * @param {DateString} datumVon
	 * @param {DateString} datumBis
	 * @param {string} betriebsstaetteID
	 * @returns array der Tagesnotizen
	 */
	async getTagesnotizenWoche(datumVon, datumBis, betriebsstaetteID) {
		try {
			const urlParams = new URLSearchParams({ DatumVon: datumVon, DatumBis: datumBis, BetriebsstaetteID: betriebsstaetteID });
			const response = await fetch(`/dienstplan/api/tagesnotiz?${urlParams.toString()}`);
			if (response.ok) {
				const notizen = await response.json();
				return notizen;
			}
			zeigeKleineNachricht('Fehler beim Laden der Tagesnotizen!', -1);
			return [];
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Fehler beim Laden der Tagesnotizen!', -1);
			return [];
		}
	}

	/**
	 * Fetch PUT Request für das Updaten einer Tagesnotiz
	 * @param {string} notiz
	 * @param {DateString} datum
	 * @param {string} betriebsstaetteID
	 */
	async fetchUpdateTagesNotiz(notiz, datum, betriebsstaetteID) {
		try {
			const body = {
				Notiz: notiz,
				Datum: datum,
				BetriebsstaetteID: betriebsstaetteID
			};
			const response = await fetch('/dienstplan/api/tagesnotiz', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(body)
			});
			if (response.ok) {
				zeigeKleineNachricht('Speichern erfolgreich!', 1);
				return true;
			}
			zeigeKleineNachricht('Speichern fehlgeschlagen!', -1);
			return false;
		} catch (error) {
			console.log(error);
			zeigeKleineNachricht('Speichern fehlgeschlagen!', -1);
			return false;
		}
	}
}

const stammdatenService = new StammdatenService();
export { stammdatenService };
